<template>
    <div class="relative w-full">
        <!-- Trigger/Pill -->
        <div
            class="flex items-center justify-between border border-new-200 rounded-full px-4 py-2.5 bg-white cursor-pointer"
            @click="togglePanel"
        >
            <div class="flex items-center gap-2">
                <i class="fa-solid fa-calendar text-[#777777]"></i>
                <span
                    class="text-sm"
                    :class="hasRange ? 'text-gray-800' : 'text-gray-500'"
                >
                    {{
                        hasRange
                            ? `${formatDisplay(
                                  modelValue.startDate
                              )} - ${formatDisplay(modelValue.endDate)}`
                            : placeholder
                    }}
                </span>
            </div>
            <div class="flex items-center gap-3">
                <button
                    v-if="hasRange"
                    type="button"
                    class="text-xs text-gray-500 hover:text-gray-700"
                    @click.stop="clearAll"
                    aria-label="Clear dates"
                >
                    Clear
                </button>
                <i class="fa-solid fa-chevron-down text-[#777777]"></i>
            </div>
        </div>

        <!-- Panel -->
        <transition name="slide-down">
            <div
                v-if="open"
                class="absolute z-50 w-full mt-2 bg-white border border-gray-200 shadow-xl rounded-2xl"
                @click.stop
            >
                <div class="grid grid-cols-1 gap-4 p-4 lg:grid-cols-12">
                    <!-- LEFT: Manual inputs -->
                    <div class="flex flex-col gap-3 lg:col-span-5">
                        <div class="flex items-center gap-2">
                            <i class="fa-regular fa-calendar"></i>
                            <span class="text-sm font-medium text-gray-700"
                                >Enter dates manually</span
                            >
                        </div>

                        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <label class="block mb-1 text-xs text-gray-500"
                                    >Check-in</label
                                >
                                <div
                                    class="flex items-center gap-2 px-3 py-2 border rounded-full"
                                    :class="
                                        startError
                                            ? 'border-red-400'
                                            : 'border-new-200'
                                    "
                                >
                                    <i
                                        class="fa-regular fa-calendar text-[#777777]"
                                    ></i>
                                    <input
                                        v-model="inputStart"
                                        type="text"
                                        class="w-full text-sm border-0 focus:ring-0"
                                        :placeholder="hint"
                                        @keydown.enter.prevent="applyInputs"
                                        @blur="onStartBlur"
                                        inputmode="numeric"
                                        aria-label="Start date"
                                    />
                                </div>
                                <p
                                    v-if="startError"
                                    class="mt-1 text-xs text-red-500"
                                >
                                    {{ startError }}
                                </p>
                            </div>

                            <div>
                                <label class="block mb-1 text-xs text-gray-500"
                                    >Check-out</label
                                >
                                <div
                                    class="flex items-center gap-2 px-3 py-2 border rounded-full"
                                    :class="
                                        endError
                                            ? 'border-red-400'
                                            : 'border-new-200'
                                    "
                                >
                                    <i
                                        class="fa-regular fa-calendar text-[#777777]"
                                    ></i>
                                    <input
                                        v-model="inputEnd"
                                        type="text"
                                        class="w-full text-sm border-0 focus:ring-0"
                                        :placeholder="hint"
                                        @keydown.enter.prevent="applyInputs"
                                        @blur="onEndBlur"
                                        inputmode="numeric"
                                        aria-label="End date"
                                    />
                                </div>
                                <p
                                    v-if="endError"
                                    class="mt-1 text-xs text-red-500"
                                >
                                    {{ endError }}
                                </p>
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <div class="text-xs text-gray-500">
                                Format:
                                <code class="font-mono">dd/MM/yyyy</code>,
                                <code class="font-mono">yyyy-MM-dd</code>,
                                <code class="font-mono">dd MMM yyyy</code>
                            </div>
                            <div class="flex gap-2">
                                <button
                                    type="button"
                                    class="text-sm px-3 py-1.5 rounded-full border border-gray-200 hover:bg-gray-50"
                                    @click="clearAll"
                                >
                                    Clear
                                </button>
                                <button
                                    type="button"
                                    class="text-sm px-4 py-1.5 rounded-full bg-primary text-white hover:bg-primary-400"
                                    @click="applyInputs"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>

                        <!-- Presets -->
                        <div v-if="presetsToShow.length" class="mt-1">
                            <div class="mb-2 text-xs text-gray-500">
                                Quick picks
                            </div>
                            <div class="flex flex-wrap gap-2">
                                <button
                                    v-for="(p, i) in presetsToShow"
                                    :key="i"
                                    type="button"
                                    class="text-xs px-3 py-1.5 rounded-full border border-new-200 hover:bg-gray-50"
                                    @click="applyPreset(p)"
                                >
                                    {{ p.label }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- RIGHT: Calendar -->
                    <div class="lg:col-span-7">
                        <div class="flex flex-col gap-3">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2">
                                    <button
                                        class="px-2 py-1 rounded hover:bg-gray-100"
                                        @click="prevMonth"
                                        aria-label="Previous month"
                                    >
                                        <i class="fa-solid fa-chevron-left"></i>
                                    </button>
                                    <div class="text-sm font-semibold">
                                        {{ monthLabel }} {{ year }}
                                    </div>
                                    <button
                                        class="px-2 py-1 rounded hover:bg-gray-100"
                                        @click="nextMonth"
                                        aria-label="Next month"
                                    >
                                        <i
                                            class="fa-solid fa-chevron-right"
                                        ></i>
                                    </button>
                                </div>
                                <div
                                    class="items-center hidden gap-1 text-xs text-gray-500 md:flex"
                                >
                                    <span
                                        class="inline-flex items-center gap-1"
                                    >
                                        <span
                                            class="rounded-full size-3 bg-primary/80"
                                        ></span>
                                        selected
                                    </span>
                                    <span
                                        class="inline-flex items-center gap-1"
                                    >
                                        <span
                                            class="bg-gray-200 border border-gray-300 rounded-full size-3"
                                        ></span>
                                        available
                                    </span>
                                </div>
                            </div>

                            <div
                                class="grid grid-cols-7 gap-1 text-xs text-center text-gray-500"
                            >
                                <div
                                    v-for="d in weekDays"
                                    :key="d"
                                    class="py-1"
                                >
                                    {{ d }}
                                </div>
                            </div>

                            <div class="grid grid-cols-7 gap-1">
                                <button
                                    v-for="(d, idx) in daysGrid"
                                    :key="idx"
                                    type="button"
                                    class="text-sm rounded-md h-9"
                                    :class="dayClass(d)"
                                    :disabled="isDisabled(d)"
                                    @click="pickDate(d)"
                                >
                                    {{ d.getDate() }}
                                </button>
                            </div>

                            <div class="flex justify-end">
                                <button
                                    type="button"
                                    class="text-sm px-4 py-1.5 rounded-full bg-primary text-white hover:bg-primary-400"
                                    @click="confirmCalendar"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import {
    format,
    parse,
    isValid,
    addMonths,
    startOfMonth,
    addDays,
} from "date-fns";

export default {
    name: "BaseSmartDateRange",
    props: {
        modelValue: {
            type: Object,
            default: () => ({ startDate: null, endDate: null }),
        },
        placeholder: {
            type: String,
            default: "Select date range",
        },
        disablePastDates: {
            type: Boolean,
            default: true,
        },
        showPrice: {
            // disimpan untuk kompatibilitas API lama (tidak dipakai di sini)
            type: Boolean,
            default: true,
        },
        presets: {
            type: Array,
            default: () => [], // [{label, start: Date, end: Date}]
        },
        displayFormat: {
            type: String,
            default: "dd MMM yyyy",
        },
        parseFormats: {
            type: Array,
            default: () => ["dd/MM/yyyy", "yyyy-MM-dd", "dd MMM yyyy"],
        },
        firstDayMonday: {
            type: Boolean,
            default: true,
        },
    },
    emits: ["update:modelValue", "change"],
    data() {
        const today = new Date();
        return {
            open: false,
            year: today.getFullYear(),
            month: today.getMonth(),

            // manual inputs
            inputStart: "",
            inputEnd: "",
            startError: "",
            endError: "",

            // temp selection from calendar before apply
            tempStart: this.modelValue.startDate
                ? new Date(this.modelValue.startDate)
                : null,
            tempEnd: this.modelValue.endDate
                ? new Date(this.modelValue.endDate)
                : null,
        };
    },
    computed: {
        hasRange() {
            return !!(this.modelValue?.startDate && this.modelValue?.endDate);
        },
        hint() {
            return this.displayFormat;
        },
        weekDays() {
            return this.firstDayMonday
                ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
                : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        },
        monthLabel() {
            return format(new Date(this.year, this.month, 1), "LLLL");
        },
        daysGrid() {
            const first = startOfMonth(new Date(this.year, this.month, 1));
            // align with Monday-first if enabled
            const weekday = first.getDay(); // 0=Sun..6=Sat
            const padStart = this.firstDayMonday ? (weekday + 6) % 7 : weekday;

            const days = [];
            for (let i = -padStart; i < 42 - padStart; i++) {
                days.push(addDays(first, i));
            }
            return days;
        },
        presetsToShow() {
            return this.presets;
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(v) {
                // sync inputs with external model
                const s = v?.startDate ? new Date(v.startDate) : null;
                const e = v?.endDate ? new Date(v.endDate) : null;
                this.inputStart = s ? this.formatDisplay(s) : "";
                this.inputEnd = e ? this.formatDisplay(e) : "";
                this.tempStart = s;
                this.tempEnd = e;
            },
        },
    },
    methods: {
        togglePanel() {
            this.open = !this.open;
        },
        formatDisplay(d) {
            try {
                return d ? format(new Date(d), this.displayFormat) : "";
            } catch {
                return "";
            }
        },
        parseAny(v) {
            // try all formats
            for (const f of this.parseFormats) {
                const d = parse(v, f, new Date());
                if (isValid(d)) return d;
            }
            // also try Date(...)
            const d2 = new Date(v);
            return isValid(d2) ? d2 : null;
        },
        validateOrder(s, e) {
            if (s && e && e < s) {
                // auto-fix: set end = start + 1 day
                const fixed = addDays(s, 1);
                return { start: s, end: fixed };
            }
            return { start: s, end: e };
        },
        onStartBlur() {
            this.startError = "";
            if (!this.inputStart) {
                this.tempStart = null;
                return;
            }
            const d = this.parseAny(this.inputStart);
            if (!d) {
                this.startError = "Invalid date";
                return;
            }
            if (this.disablePastDates && d < this.todayZero()) {
                this.startError = "Cannot select past date";
                return;
            }
            this.tempStart = d;
            if (this.tempEnd && this.tempEnd < d) this.tempEnd = addDays(d, 1);
            this.inputStart = this.formatDisplay(d);
        },
        onEndBlur() {
            this.endError = "";
            if (!this.inputEnd) {
                this.tempEnd = null;
                return;
            }
            const d = this.parseAny(this.inputEnd);
            if (!d) {
                this.endError = "Invalid date";
                return;
            }
            if (this.disablePastDates && d < this.todayZero()) {
                this.endError = "Cannot select past date";
                return;
            }
            // if start exists and end <= start -> bump end
            if (this.tempStart && d <= this.tempStart) {
                this.endError = "End must be after start";
                this.tempEnd = addDays(this.tempStart, 1);
                this.inputEnd = this.formatDisplay(this.tempEnd);
                return;
            }
            this.tempEnd = d;
            this.inputEnd = this.formatDisplay(d);
        },
        applyInputs() {
            this.onStartBlur();
            this.onEndBlur();
            if (this.startError || this.endError) return;

            const { start, end } = this.validateOrder(
                this.tempStart,
                this.tempEnd
            );
            if (!start || !end) return;

            this.commitRange(start, end);
        },
        confirmCalendar() {
            if (!this.tempStart || !this.tempEnd) return;
            this.commitRange(this.tempStart, this.tempEnd);
        },
        commitRange(s, e) {
            this.$emit("update:modelValue", { startDate: s, endDate: e });
            this.$emit("change", { startDate: s, endDate: e });
            this.open = false;
        },
        clearAll() {
            this.inputStart = "";
            this.inputEnd = "";
            this.startError = "";
            this.endError = "";
            this.tempStart = null;
            this.tempEnd = null;
            this.$emit("update:modelValue", { startDate: null, endDate: null });
            this.$emit("change", { startDate: null, endDate: null });
        },
        applyPreset(p) {
            if (!p?.start || !p?.end) return;
            const { start, end } = this.validateOrder(
                new Date(p.start),
                new Date(p.end)
            );
            this.tempStart = start;
            this.tempEnd = end;
            this.inputStart = this.formatDisplay(start);
            this.inputEnd = this.formatDisplay(end);
            this.commitRange(start, end);
        },
        pickDate(d) {
            if (this.isDisabled(d)) return;
            if (!this.tempStart || (this.tempStart && this.tempEnd)) {
                this.tempStart = d;
                this.tempEnd = null;
            } else if (!this.tempEnd) {
                const { start, end } = this.validateOrder(this.tempStart, d);
                this.tempStart = start;
                this.tempEnd = end;
            }
            this.inputStart = this.tempStart
                ? this.formatDisplay(this.tempStart)
                : "";
            this.inputEnd = this.tempEnd
                ? this.formatDisplay(this.tempEnd)
                : "";
        },
        isDisabled(d) {
            if (this.disablePastDates && d < this.todayZero()) return true;
            // disable days outside current month display (optional visual)
            return false;
        },
        dayClass(d) {
            const inMonth = d.getMonth() === this.month;
            const isStart = this.tempStart && this.sameDate(d, this.tempStart);
            const isEnd = this.tempEnd && this.sameDate(d, this.tempEnd);
            const inRange =
                this.tempStart &&
                this.tempEnd &&
                d >= this.stripTime(this.tempStart) &&
                d <= this.stripTime(this.tempEnd);

            return [
                inMonth ? "text-gray-700" : "text-gray-300",
                "hover:bg-gray-100",
                (isStart || isEnd) && "bg-primary text-white hover:bg-primary",
                inRange && !isStart && !isEnd && "bg-primary/10",
                "border border-transparent",
            ]
                .filter(Boolean)
                .join(" ");
        },
        sameDate(a, b) {
            return (
                a.getFullYear() === b.getFullYear() &&
                a.getMonth() === b.getMonth() &&
                a.getDate() === b.getDate()
            );
        },
        stripTime(d) {
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
        },
        todayZero() {
            const t = new Date();
            return new Date(t.getFullYear(), t.getMonth(), t.getDate());
        },
        prevMonth() {
            const d = addMonths(new Date(this.year, this.month, 1), -1);
            this.year = d.getFullYear();
            this.month = d.getMonth();
        },
        nextMonth() {
            const d = addMonths(new Date(this.year, this.month, 1), 1);
            this.year = d.getFullYear();
            this.month = d.getMonth();
        },
    },
};
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}
.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
